export interface ArchiveYearState<T = any> {
  items: T[]
  page: number
  hasMore: boolean
  loadingMore: boolean
}

const INITIAL_VISIBLE_YEARS = 6

// Pure helper — parses API response into years[] + archiveStore record
function parseArchiveResponse<T>(res: any): {
  years: number[]
  store: Record<number, ArchiveYearState<T>>
} {
  const years: number[] = []
  const store: Record<number, ArchiveYearState<T>> = {}

  if (Array.isArray(res)) {
    res.forEach((entry) => {
      const year: number = entry.year
      const items: T[] = entry.items || []
      years.push(year)
      store[year] = {
        items,
        page: 1,
        hasMore: entry.paginatorInfo
          ? entry.paginatorInfo.hasMorePages
          : items.length >= 5,
        loadingMore: false,
      }
    })
  } else if (res?.items) {
    const grouped: Record<number, T[]> = {}
    ;(res.items as any[]).forEach((item) => {
      const dateStr = item.starts_at || item.created_at || item.published_at
      const year = dateStr
        ? new Date(dateStr).getFullYear()
        : new Date().getFullYear()
      if (!grouped[year]) grouped[year] = []
      grouped[year]!.push(item)
    })
    Object.keys(grouped)
      .map(Number)
      .sort((a, b) => b - a)
      .forEach((year) => {
        years.push(year)
        store[year] = {
          items: grouped[year]!,
          page: res.page || 1,
          hasMore:
            res.hasMore !== undefined
              ? res.hasMore
              : grouped[year]!.length >= 5,
          loadingMore: false,
        }
      })
  }

  return { years, store }
}

export const useArchive = async <T = any>(
  type: string,
  perPage: number,
  filterSlug?: Ref<string | undefined>,
) => {
  const allYears = ref<number[]>([])
  const archiveStore = ref<Record<number, ArchiveYearState<T>>>({})
  const visibleYearsCount = ref(INITIAL_VISIBLE_YEARS)

  const { locale } = useI18n()
  const runtimeConfig = useRuntimeConfig()
  const apiUrl = runtimeConfig.public.httpEndpoint

  const years = computed(() => allYears.value.slice(0, visibleYearsCount.value))

  const hasMoreYears = computed(
    () => allYears.value.length > visibleYearsCount.value,
  )

  const hasData = computed(() => allYears.value.length > 0)

  // tagSlugs as a computed ref so usePersistedFetch re-resolves it on every
  // re-fetch (toValue() is called inside getCleanVariables()).
  const tagSlugsRef = computed<string[] | undefined>(() => {
    if (!filterSlug) return undefined
    const slug = filterSlug.value
    if (!slug || slug === 'vse' || slug === 'all') return undefined
    return [slug]
  })

  // usePersistedFetch MUST be called at the TOP LEVEL of setup.
  // Passing tagSlugsRef as a value: usePersistedFetch calls toValue() on each
  // variable, so it resolves the current ref value on every re-fetch.
  const { data, pending: pendingInitial } = await usePersistedFetch(
    'GetArchivItems',
    { type, perPage, tagSlugs: tagSlugsRef } as any,
    {
      // Drive re-fetch when filter changes
      watch: filterSlug ? [filterSlug] : [],
    },
  )

  // Populate (or reset) the reactive store from raw API data
  const syncStore = (raw: any) => {
    const res = raw?.getArchivItems
    if (!res) {
      allYears.value = []
      archiveStore.value = {}
      visibleYearsCount.value = INITIAL_VISIBLE_YEARS
      return
    }
    const { years: y, store } = parseArchiveResponse<T>(res)
    allYears.value = y
    archiveStore.value = store
    visibleYearsCount.value = INITIAL_VISIBLE_YEARS
  }

  // Initial population (SSR)
  syncStore(data.value)

  // Re-populate when the filter changes and useAsyncData re-fetches
  watch(data, (newData) => syncStore(newData))

  const loadMoreForYear = async (year: number) => {
    const yearState = archiveStore.value[year]

    if (!yearState || yearState.loadingMore || !yearState.hasMore) return

    yearState.loadingMore = true
    const nextPage = yearState.page + 1
    const tagSlugs = tagSlugsRef.value

    try {
      const result: any = await $fetch(apiUrl, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'content-language': locale.value,
        },
        body: {
          operationName: 'GetArchivItems',
          queryId: 'GetArchivItems',
          variables: {
            type,
            year,
            page: nextPage,
            perPage,
            ...(tagSlugs ? { tagSlugs } : {}),
          },
        },
        credentials: 'include',
      })

      const res = result?.data?.getArchivItems
      const yearData = Array.isArray(res) ? res[0] : res
      const newItems = yearData?.items || (Array.isArray(res) ? res : [])

      yearState.items.push(...newItems)
      yearState.page = nextPage

      if (yearData && yearData.hasMore !== undefined) {
        yearState.hasMore = yearData.hasMore
      } else {
        const paginator = yearData?.paginatorInfo
        yearState.hasMore = paginator
          ? paginator.hasMorePages
          : newItems.length >= 5
      }
    } finally {
      yearState.loadingMore = false
    }
  }

  const loadMoreYears = () => {
    visibleYearsCount.value += INITIAL_VISIBLE_YEARS
  }

  return {
    years,
    archiveStore,
    loadMoreForYear,
    loadMoreYears,
    hasMoreYears,
    hasData,
    pendingInitial,
  }
}
